/**
 * What a task is worth after N wrong attempts.
 *
 * Three modes, and nothing else:
 *
 *   - `ungraded`  — no score at all. Not zero points; no points.
 *   - `milestone` — full points on completion, however many attempts it took.
 *   - `penalty`   — base points minus the penalty per wrong attempt, floored at zero.
 *
 * ## Why the floor is not optional
 *
 * SPR, the system this replaces, let a task's score run negative: 5 points with a 1-point penalty
 * counted 5, 4, 3, 2, 1, 0, −1, −2, −3, −4, and reset to zero when the student navigated away. Two
 * separate defects. The negative tail meant one task could drag a whole experience total down and
 * the dataset carried scores that were never earned; the reset meant a score depended on ROUTING,
 * which makes it unanalysable by construction. Neither is reproduced here.
 *
 * The struggle signal that the negative tail was carrying belongs in the attempt COUNT, which is
 * recorded separately and already exists. Score answers "how much credit"; attempts answers "how
 * many tries". Conflating them is what made the old data unreadable.
 *
 * ## Reachable, not overshot
 *
 * The floor must be attainable. With 5 points and a 3-point penalty the ladder is 5 → 2 → 0: the
 * second mistake costs the remaining 2 rather than the full 3. It does not strand at 2 because a
 * full step would go negative, and it does not pass through −1. `scoreLadder` exists so the
 * authoring UI can show that uneven last step rather than leaving a teacher to discover it.
 *
 * ## Arithmetic in hundredths
 *
 * Points and penalties carry two decimal places. `scoreTask` could get away with floats most of
 * the time, since one `points - n * penalty` usually lands clean — but `scoreLadder` steps DOWN
 * one penalty at a time, and that is where binary floats produce the 0.9299999999999999 that ends
 * up in someone's dataset. Both convert to integer hundredths first and convert back once at the
 * end, so the two can never disagree about what a task is worth.
 */
/** Points are authored between zero and this. A task worth more than 100 is a rubric, not a task. */
export const MAX_POINTS = 100;
/** Two decimal places, as hundredths. */
export const SCORE_PRECISION = 100;
/** To exact integer hundredths, so repeated subtraction cannot drift. */
export function toHundredths(value) {
    return Math.round(value * SCORE_PRECISION);
}
/** Back to points, at two decimal places. */
export function fromHundredths(hundredths) {
    return Math.round(hundredths) / SCORE_PRECISION;
}
/**
 * Round an authored figure onto the 2dp grid.
 *
 * Applied at the point of entry rather than at the point of use: a value that reaches storage with
 * three decimals is a value every later total has to keep apologising for.
 */
export function roundScore(value) {
    return fromHundredths(toHundredths(value));
}
/**
 * The credit earned for a task after `wrongAttempts` wrong tries.
 *
 * `null` means UNGRADED — genuinely no score, which is a different statement from zero and must
 * not be summed as one. A task the student scored 0 on attempted and failed; an ungraded task was
 * never worth anything.
 */
export function scoreTask(task, wrongAttempts) {
    const mode = task.scoringMode ?? 'ungraded';
    if (mode === 'ungraded')
        return null;
    const points = toHundredths(Math.max(0, task.points ?? 0));
    if (mode === 'milestone')
        return fromHundredths(points);
    // Negative attempt counts are a caller bug, not a score of more than full marks.
    const wrong = Math.max(0, Math.floor(wrongAttempts));
    const penalty = toHundredths(Math.max(0, task.penalty ?? 0));
    // The floor lives here, and it is the ONLY place a penalty score can end up below zero.
    return fromHundredths(Math.max(0, points - wrong * penalty));
}
/**
 * Every value the score passes through, from full marks down to the floor.
 *
 * For the authoring UI: a teacher setting 5 points against a 3-point penalty should be able to see
 * `5, 2, 0` and decide whether that uneven last step is what they meant. Ends at the first zero —
 * further wrong attempts change nothing, so listing them would imply otherwise.
 *
 * Returns `[]` for a mode with no ladder, and for a zero penalty, which never descends.
 */
export function scoreLadder(task) {
    if ((task.scoringMode ?? 'ungraded') !== 'penalty')
        return [];
    const points = toHundredths(Math.max(0, task.points ?? 0));
    const penalty = toHundredths(Math.max(0, task.penalty ?? 0));
    if (points <= 0 || penalty <= 0)
        return [];
    const steps = [];
    for (let value = points;; value = Math.max(0, value - penalty)) {
        steps.push(fromHundredths(value));
        if (value === 0)
            break;
    }
    return steps;
}
/**
 * How many wrong attempts it takes to reach zero.
 *
 * `Math.ceil` rather than a division, because the last step is the short one: 5 points against a
 * 3-point penalty bottoms out on the SECOND mistake, not on the 1.67th.
 */
export function attemptsToFloor(task) {
    if ((task.scoringMode ?? 'ungraded') !== 'penalty')
        return null;
    const points = toHundredths(Math.max(0, task.points ?? 0));
    const penalty = toHundredths(Math.max(0, task.penalty ?? 0));
    if (penalty <= 0)
        return null;
    return Math.ceil(points / penalty);
}
/**
 * Whether the penalty divides the points evenly.
 *
 * Not an error — an uneven ladder is allowed, and 5 against 3 is a perfectly reasonable thing to
 * author. It is a thing the UI should be able to SAY, so the short final step is a decision rather
 * than a surprise.
 */
export function laddersEvenly(task) {
    if ((task.scoringMode ?? 'ungraded') !== 'penalty')
        return true;
    const points = toHundredths(Math.max(0, task.points ?? 0));
    const penalty = toHundredths(Math.max(0, task.penalty ?? 0));
    if (penalty <= 0)
        return true;
    return points % penalty === 0;
}
/**
 * Clamp an authored pair onto the rules: 0 ≤ points ≤ MAX_POINTS, 0 ≤ penalty ≤ points, both at 2dp.
 *
 * `penalty ≤ points` is the rule that makes an absurd penalty unauthorable rather than merely
 * harmless. A penalty above the points is already a no-op once the score floors at zero, so
 * allowing it only lets a teacher believe they have configured something they have not — which is
 * how a 4349203-point penalty came to sit in real data.
 *
 * The invariant has to be applied at the TASK level and not only to the experience default,
 * because the default seeds a task at creation and the task's points can be lowered afterwards.
 */
export function clampScoring(points, penalty) {
    const p = Math.min(MAX_POINTS, Math.max(0, roundScore(Number.isFinite(points) ? points : 0)));
    const q = Math.min(p, Math.max(0, roundScore(Number.isFinite(penalty) ? penalty : 0)));
    return { points: p, penalty: q };
}
//# sourceMappingURL=scoring.js.map